import styled from "styled-components";
import useTheme from "../../../../common/hooks/useTheme";
import { forwardRef, ReactNode } from "react";

// Scroll container with a stable, non-overlay scrollbar.
//
// Pseudo-elements (::-webkit-scrollbar) can't be set via inline styles, so this
// is a styled component. `scrollbar-gutter: stable` reserves the gutter whether
// or not the content overflows, and the explicit `::-webkit-scrollbar` width
// forces a classic (space-taking) scrollbar in WebKit. Together they stop the
// scrollbar from (a) floating over the content on trackpad/overlay-scrollbar
// setups and (b) shrinking the content width when a mouse switches to a classic
// scrollbar — the width stays constant in both cases.
//
// That constant width is the right trade for content cards, where a 1px jump on
// overflow is worse than a permanent gutter. Containers where the empty gutter
// reads as a visual gap (the sidebar nav, which usually doesn't overflow) can
// pass `reserveGutter={false}` to fall back to `scrollbar-gutter: auto` — the
// bar then costs space only on the rare occasion it actually appears.
const Wrapper = styled.div<{
  $padding: string;
  $thumb: string;
  $hidden: boolean;
  $reserveGutter: boolean;
}>`
  padding-right: ${({ $padding }) => $padding};
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 8px;

  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: ${({ $hidden, $reserveGutter }) =>
    $hidden || !$reserveGutter ? "auto" : "stable"};

  /* IMPORTANT: do NOT set scrollbar-width / scrollbar-color for the visible
     scrollbar at the top level. In Chrome 121+, setting either makes the browser
     use the standard (overlay, floats-over-content) scrollbar and IGNORE the
     ::-webkit-scrollbar rules below. We rely on ::-webkit-scrollbar to force a
     fixed-width, space-taking, non-overlay bar. Only the hidden variant sets
     scrollbar-width: none (Firefox) since there's no bar to keep classic. */
  ${({ $hidden }) => ($hidden ? "scrollbar-width: none;" : "")}

  /* Firefox ignores ::-webkit-scrollbar entirely, so without this it falls back
     to its native thick bar with an opaque track (see the tester's recording).
     We must scope the standard scrollbar-* props to Firefox ONLY — if Chrome
     sees them it drops the ::-webkit-scrollbar path (the Chrome 121+ overlay
     regression noted above). @supports NOT selector(::-webkit-scrollbar) does
     NOT work as the guard: Firefox reports that selector as supported (for
     web-compat), so the block never applied there. @supports (-moz-appearance)
     is the reliable Firefox-only query — false in Chrome/Safari, true in
     Firefox. This gives Firefox a transparent track (2nd scrollbar-color value)
     and a thin thumb. The hidden variant already sets scrollbar-width: none.

     On macOS + trackpad, Firefox uses OS OVERLAY scrollbars: the thin bar
     floats over the content and scrollbar-gutter:stable has NO effect
     (overlay bars don't consume space, so there's no gutter to reserve).
     Unlike Chrome, Firefox has no way to force a classic space-taking bar, so
     we reserve the gutter manually with padding-right — this keeps the floating
     bar off the card content. max() preserves any larger customPadding a
     consumer already passed. That reservation is skipped when the consumer opts
     out of the gutter (see $reserveGutter); the thin/transparent styling still
     applies so an overflowing Firefox container doesn't get the native bar. */
  ${({ $hidden, $reserveGutter, $thumb, $padding }) =>
    $hidden
      ? ""
      : `@supports (-moz-appearance: none) {
    scrollbar-width: thin;
    scrollbar-color: ${$thumb} transparent;${
          $reserveGutter
            ? `
    padding-right: max(${$padding === "0" ? "0px" : $padding}, 8px);`
            : ""
        }
  }`}

  /* WebKit (Chrome/Safari): a fixed-width, non-overlay scrollbar that sits in
     the reserved gutter instead of floating over the content. */
  &::-webkit-scrollbar {
    width: ${({ $hidden }) => ($hidden ? "0px" : "8px")};
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ $hidden, $thumb }) => ($hidden ? "transparent" : $thumb)};
    border-radius: 10px;
    /* Transparent border + padding-box clip insets the thumb from the track
       edges, leaving a gap between the thumb and the content/container. With
       the 8px track this yields a ~4px visible thumb with a 2px gap each side. */
    border: 2px solid transparent;
    background-clip: padding-box;
  }
`;

const ScrollableWrapper = forwardRef<
  HTMLDivElement,
  {
    children: ReactNode;
    isScrollBarHidden?: boolean;
    customPadding?: string;
    // Use on dark surfaces (e.g. the navy sidebar): renders a white thumb
    // instead of the default neutral-gray one so it stays visible.
    isDark?: boolean;
    // Reserve the scrollbar's width even when the content fits (default). Set
    // false when an always-reserved gutter would look like a gap.
    reserveGutter?: boolean;
  }
>(
  (
    {
      children,
      isScrollBarHidden = false,
      customPadding = "0",
      isDark = false,
      reserveGutter = true,
    },
    ref
  ) => {
    const theme = useTheme();
    const thumb = isDark
      ? theme.colors_ds.NEUTRAL_GENERAL_WHITE
      : theme.colors_ds.NEUTRAL_BORDER_DEFAULT;
    return (
      <Wrapper
        ref={ref}
        $padding={customPadding}
        $hidden={isScrollBarHidden}
        $thumb={thumb}
        $reserveGutter={reserveGutter}
      >
        {children}
      </Wrapper>
    );
  }
);

export default ScrollableWrapper;
